import { Fragment } from 'react';

import { Subsection } from '@/components/section/section';
import { Tooltip } from '@/components/tooltip/tooltip';
import { DailyCount, TopModelsByDay } from '@/types/observability';
import { gradientColor } from '@/utils/colorUtils';

import { formatChartDate } from './analyticsUtils';
import styles from './topModelsSection.module.scss';

// Global heat map gradient: dark (low %) → bright (high %)
// Applied identically to all cells so any two cells are directly comparable.
const HEAT_GRADIENT = [
  { stop: 0, hex: '#1A0030' }, // very dark purple
  { stop: 0.33, hex: '#8C1A0E' }, // dark red
  { stop: 0.67, hex: '#D85C20' }, // orange
  { stop: 1, hex: '#F5C832' } // bright yellow
];

function TopModelsByDayChart( { data, invocationsTrend }: {
  data: TopModelsByDay[];
  invocationsTrend: DailyCount[];
} ) {
  if ( data.length === 0 ) return null;

  const modelTotals = new Map<string, number>();
  data.forEach( day => day.models.forEach( m => modelTotals.set( m.model, ( modelTotals.get( m.model ) ?? 0 ) + m.percentage ) ) );
  const topModels = Array.from( modelTotals.entries() )
    .sort( ( a, b ) => b[1] - a[1] )
    .slice( 0, 3 )
    .map( ( [ model ] ) => model );

  const invocationsMap = new Map( invocationsTrend.map( d => [ d.date, d.count ] ) );

  const allPcts = topModels.flatMap( model => data.map( day => day.models.find( m => m.model === model )?.percentage ?? 0 ) );
  const globalMin = Math.min( ...allPcts );
  const globalMax = Math.max( ...allPcts );

  return (
    <div className={ styles.topModelsGrid } style={ { gridTemplateColumns: `max-content repeat(${ data.length }, 1fr)` } }>
      {topModels.map( ( model ) => (
        <Fragment key={ model }>
          <div className={ styles.topModelsModelLabel }>{ model }</div>
          {data.map( ( day, dateIdx ) => {
            const entry = day.models.find( m => m.model === model );
            const pct = entry?.percentage ?? 0;
            const dailyCount = invocationsMap.get( day.date );
            const count = dailyCount != null ? Math.round( dailyCount * pct / 100 ) : null;
            const [ yr, mo, dy ] = day.date.split( '-' ).map( Number );
            const monthAbbr = new Date( yr, mo - 1, 1 ).toLocaleString( 'en-US', { month: 'short' } );
            const tooltipDate = `${ monthAbbr }-${ String( dy ).padStart( 2, '0' ) }-${ yr }`;
            const tooltipContent = (
              <>
                <div>{ tooltipDate }</div>
                <div>{ count != null ? `${ count.toLocaleString() } calls (${ pct.toFixed( 1 ) }%)` : `${ pct.toFixed( 1 ) }%` }</div>
                <div>{ model }</div>
              </>
            );
            const bgColor = gradientColor( pct, globalMin, globalMax, HEAT_GRADIENT );
            return (
              <Tooltip key={ `${ model }-${ dateIdx }` } content={ tooltipContent } position="top" className={ styles.topModelsCellTooltip }>
                <div className={ styles.topModelsCell } style={ { backgroundColor: bgColor } }>
                  { pct.toFixed( 1 ) }%
                </div>
              </Tooltip>
            );
          } )}
        </Fragment>
      ) )}
      <div />
      {data.map( day => (
        <div key={ day.date } className={ styles.topModelsDateLabel }>
          { formatChartDate( day.date ) }
        </div>
      ) )}
    </div>
  );
}

export function TopModelsSection( { data, invocationsTrend, timeRangeLabel }: {
  data: TopModelsByDay[];
  invocationsTrend: DailyCount[];
  timeRangeLabel: string;
} ) {
  return (
    <Subsection testId="analytics-top-models" title={ `Top 3 Models ${ timeRangeLabel }` }>
      { data.length === 0
        ? <p className={ styles.noData }>No model usage data for this period.</p>
        : <TopModelsByDayChart data={ data } invocationsTrend={ invocationsTrend } />}
    </Subsection>
  );
}
